import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMessenger } from "@/context/MessengerContext";
import { toast } from "sonner";
import { PaperPlaneRight, X, Minus, ArrowUpRight, ChatCircleDots } from "@phosphor-icons/react";
import UserBadge from "@/components/UserBadge";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

function timeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

function Avatar({ user, size = 28 }) {
  const src = resolveImg(user?.avatar_url);
  const letter = (user?.username || "?").slice(0, 1).toUpperCase();
  return src ? (
    <img src={src} alt={user?.username} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />
  ) : (
    <div style={{ width: size, height: size }} className="rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black shrink-0 text-[11px]">
      {letter}
    </div>
  );
}

function IMWindow({ other, minimized, index, onClose, onToggle }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/thread/${other.id}`);
      setMessages(data.messages || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [other.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (minimized) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load, minimized]);

  useEffect(() => {
    if (!minimized) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, minimized]);

  async function send(e) {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const trimmed = body.trim();
      setBody("");
      const { data } = await api.post(`/messages/thread/${other.id}`, { body: trimmed });
      setMessages((m) => [...m, data]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  // stack from bottom-right; each window 340px wide + 12px gap
  const rightOffset = 24 + index * (340 + 12);
  // While a user is on /surf, the top surf bar occupies the top strip of the
  // viewport. Cap the popup so its top edge never reaches (or overlaps) that
  // bar. On all other pages, keep the softer half-viewport cap.
  const location = useLocation();
  const isSurfing = location.pathname.startsWith("/surf");
  const maxHeight = isSurfing ? "min(50vh, calc(100vh - 96px))" : "50vh";

  return (
    <div
      data-testid={`im-window-${other.id}`}
      style={{ right: rightOffset, maxHeight }}
      className="fixed bottom-0 z-[60] w-[340px] shadow-2xl rounded-t-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2 bg-blue-700 text-white cursor-pointer select-none"
        onClick={() => onToggle(other.id)}
        data-testid={`im-header-${other.id}`}
      >
        <Avatar user={other} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black truncate inline-flex items-center gap-1">{other.username}<UserBadge user={other} size={12} /></div>
          {other.membership && other.membership !== "Free" && (
            <div className="text-[10px] font-bold uppercase tracking-widest text-yellow-200">{other.membership}</div>
          )}
        </div>
        <Link
          to={`/messages/${other.id}`}
          onClick={(e) => e.stopPropagation()}
          className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-white/20 transition-colors duration-200"
          aria-label="Open full chat"
          data-testid={`im-expand-${other.id}`}
        >
          <ArrowUpRight size={14} weight="bold" />
        </Link>
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(other.id); }}
          className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-white/20 transition-colors duration-200"
          aria-label={minimized ? "Expand" : "Minimize"}
        >
          <Minus size={14} weight="bold" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(other.id); }}
          className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-white/20 transition-colors duration-200"
          aria-label="Close"
          data-testid={`im-close-${other.id}`}
        >
          <X size={14} weight="bold" />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-1.5 bg-slate-50 dark:bg-slate-950/50 min-h-[180px]">
            {loading && <div className="text-xs text-slate-500 text-center py-4">Loading…</div>}
            {!loading && messages.length === 0 && (
              <div className="text-xs text-slate-500 dark:text-slate-400 text-center py-6">
                No messages yet — say hi to <b>{other.username}</b> 👋
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`im-msg-${m.id}`}>
                  <div className={`max-w-[80%] px-3 py-1.5 text-[13px] leading-snug rounded-2xl ${mine ? "bg-blue-700 text-white rounded-br-sm" : "bg-slate-200 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-sm"}`}>
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    <div className={`text-[9px] mt-0.5 text-right ${mine ? "text-white/70" : "text-slate-500 dark:text-slate-400"}`}>{timeShort(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="border-t border-slate-200 dark:border-slate-700 p-2 flex items-center gap-1.5">
            <input
              data-testid={`im-input-${other.id}`}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Aa"
              maxLength={2000}
              autoFocus
              className="flex-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-blue-500 focus:outline-none px-3 py-1.5 text-sm text-slate-900 dark:text-slate-100"
            />
            <button
              type="submit"
              disabled={sending || !body.trim()}
              data-testid={`im-send-${other.id}`}
              className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white transition-colors duration-200"
              aria-label="Send"
            >
              <PaperPlaneRight size={14} weight="fill" />
            </button>
          </form>
        </>
      )}
      {minimized && (
        <div className="px-3 py-1.5 text-[11px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-950/50 flex items-center gap-1">
          <ChatCircleDots size={12} weight="bold" /> Click to expand
        </div>
      )}
    </div>
  );
}

export default function MessengerHost() {
  const { user } = useAuth();
  const { windows, closeWindow, toggleMinimize } = useMessenger();
  if (!user) return null;
  return (
    <>
      {windows.map((w, i) => (
        <IMWindow
          key={w.user.id}
          other={w.user}
          minimized={w.minimized}
          index={i}
          onClose={closeWindow}
          onToggle={toggleMinimize}
        />
      ))}
    </>
  );
}
